import React, { useEffect, useState, useRef } from "react";
import { api, rupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bot, Send, Loader2, User } from "lucide-react";

const SUGGESTIONS = [
  "Berapa total tunggakan anak saya?",
  "Bagaimana cara mencicil biaya kitab?",
  "Kapan batas pembayaran biaya bulanan?",
];

export default function Assistant() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([
    { role: "bot", text: `Assalamu'alaikum ${user?.name?.split(" ")[0] || ""} 👋 Saya asisten pembayaran Al-Khoir. Ada yang bisa saya bantu seputar tagihan, saldo, atau cara pembayaran?` },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim() || loading) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg });
      setMessages((m) => [...m, { role: "bot", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", text: "Maaf, terjadi kendala. Coba lagi sebentar." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-9rem)] lg:h-[calc(100vh-6rem)]">
      <PageHeader title="Asisten AI" subtitle="Tanya apa saja tentang pembayaran & saldo anak Anda." />

      <div className="flex-1 overflow-y-auto bg-white border border-stone-200 rounded-2xl p-4 md:p-6 space-y-4 shadow-sm">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${i}`}>
            <div className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center ${m.role === "user" ? "bg-stone-200 text-stone-600" : "bg-emerald-600 text-white"}`}>
              {m.role === "user" ? <User size={16} /> : <Bot size={16} />}
            </div>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-700"}`}>
              {m.text}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center"><Bot size={16} /></div>
            <div className="bg-stone-100 rounded-2xl px-4 py-3"><Loader2 size={16} className="animate-spin text-stone-400" /></div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full bg-white border border-stone-200 text-stone-600 hover:border-emerald-400 hover:text-emerald-700 transition-all">
              {s}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} data-testid="chat-input" placeholder="Ketik pertanyaan…" className="rounded-xl py-6" />
        <Button type="submit" disabled={loading} data-testid="chat-send-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl px-5">
          <Send size={18} />
        </Button>
      </form>
    </div>
  );
}
